import React, { useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import dxForm from 'devextreme/ui/form';
import { get, has } from 'lodash';
import Form, { FormAction, FormRefType } from '#/common/components/Form';
import useToast from '#/common/hooks/useToast';
import { UserRole } from '#/common/types/userTypes';
import useSession from '#/common/hooks/useSession';
import type { Manufacturer } from '#/graphql/types';
import useManufacturerUpdate from '../../hooks/useUpdateManufacturerMutation';
import useManufacturerCreate from '../../hooks/useCreateManufacturerMutation';
import useManufacturerDelete from '../../hooks/useDeleteManufacturerMutation';
import { createFields, updateFields } from './fields';
import { inUseStatuses } from '../../utils/constants';
import allFields from '../../utils/fields';
import { getCleanFormErrors } from '#/common/utils/customRelatedItemsValidation';
import { InitializedEventInfo } from 'devextreme/events';
import { setEditorOptions } from '#/common/components/Form/formItemOption';

export interface ManufacturerFormProps {
  manufacturer: Manufacturer | {};
  title: string;
}

const formReadonlyRole: Record<UserRole, boolean> = {
  [UserRole.asset_manager]: false,
  [UserRole.trane_admin]: false,
  [UserRole.technician]: true,
};

const ManufacturerForm: React.FC<ManufacturerFormProps> = ({ manufacturer, title }) => {
  const { alert, confirm } = useToast();
  const navigate = useNavigate();
  const { user } = useSession();
  const role = user?.role as UserRole;
  const formRef = useRef<FormRefType | null>(null);

  const id = get(manufacturer, 'id', undefined);
  const fields = id ? updateFields : createFields;

  // Defined in the modules hooks dir
  const { updateManufacturer } = useManufacturerUpdate();
  const { createManufacturer } = useManufacturerCreate();
  const { deleteManufacturer } = useManufacturerDelete();

  const deleteItem = useCallback(
    (id: string) => {
      if (!id) {
        return undefined;
      }
      return deleteManufacturer(id);
    },
    [id, deleteManufacturer],
  );

  // Submit that updates or creates a new record based on if there is ID present
  const submit = useCallback(
    (data: Manufacturer) => {
      if (!id) {
        return createManufacturer(data);
      }
      return updateManufacturer(id, data);
    },
    [id, updateManufacturer, createManufacturer],
  );

  const isFormReadonly =
    formReadonlyRole[role] ||
    (has(manufacturer, 'thirdPartyId') &&
      has(manufacturer, 'thirdPartySource') &&
      !!(manufacturer.thirdPartyId && manufacturer.thirdPartySource));

  const actions = useMemo(() => {
    const result: FormAction<Manufacturer>[] = [];

    if (
      !isFormReadonly &&
      get(manufacturer, 'inUseStatus') === inUseStatuses.newlyCreated.key &&
      deleteItem
    ) {
      result.push({
        label: 'Delete',
        icon: 'trash',
        type: 'danger',
        onClick: async (e) => {
          if (!e.record?.id) {
            return;
          }

          const sure = await confirm(
            'error',
            `Are you sure you want to delete manufacturer: ${e.record?.name}?`,
          );

          if (!sure) {
            return;
          }

          e.setIsLoading(true);
          await deleteItem(e.record.id);
          e.setIsLoading(false);

          navigate('/assets/manufacturers');
        },
      });
    }
    if (!isFormReadonly && submit) {
      result.push({
        label: 'Submit',
        type: 'submit',
        onClick: async (e) => {
          if (!e.record) {
            return;
          }
          const validation = e.form?.validate();
          if (!validation?.isValid && getCleanFormErrors(validation, allFields, e.record).length) {
            void alert('error', `${title} is not valid, check field entries.`);
            return;
          }

          if (
            e.record.inUseStatus === inUseStatuses.inUse.key &&
            has(manufacturer, 'inUseStatus') &&
            manufacturer.inUseStatus !== inUseStatuses.inUse.key
          ) {
            const sure = await confirm(
              'warning',
              'Once set to In-use, this Manufacturer cannot be deleted. Do you want to proceed?',
            );
            if (!sure) {
              return;
            }
          }

          try {
            e.setIsLoading(true);
            const result = await submit(e.record);
            e.setIsLoading(false);

            if (result?.errors) {
              void alert('error', result.errors.join(`;\n`));
              return;
            }
            if (result?.data && e.form) {
              Object.entries(result.data).forEach(([key, value]) => {
                e.form!.updateData(key, value);
              });
            }
            if (!id && result && result.data?.id) {
              navigate(`/assets/manufacturers/${result.data?.id}`, { replace: true });
            }

            e.clearDirty();
            void alert('success', 'Changes saved!');
            formRef.current?.repaint();
          } catch (e: any) {
            e.setIsLoading(false);
            console.log('Error', e);
            void alert('error', 'Missing or invalid data!');
          }
        },
      });
    }

    return result;
  }, [alert, confirm, navigate, submit, manufacturer, deleteItem]);

  const onContentReady = useCallback((e: InitializedEventInfo<dxForm>) => {
    const { component: form } = e;
    if (!form) return;
    const formData = form.option('formData');
    if (formData?.inUseStatus !== inUseStatuses.newlyCreated.key) {
      const inUseStatusOptions = {
        dataSource: Object.values({
          ...inUseStatuses,
          newlyCreated: { ...inUseStatuses.newlyCreated, disabled: true },
        }),
      };
      setEditorOptions(form, 'inUseStatus', inUseStatusOptions);
    }
  }, []);

  return (
    <Form
      ref={formRef}
      supportTenant
      readonly={isFormReadonly}
      record={manufacturer as Manufacturer}
      fields={fields}
      actions={actions}
      onContentReady={onContentReady}
    />
  );
};

export default React.memo(ManufacturerForm);
